/**
 * Crypto primitives pro keystore.
 *
 * - PBKDF2 (SHA-256) deriva uma chave AES-GCM da senha do usuário.
 *   (Argon2id seria mais resistente a brute-force, mas exigiria WASM
 *   externo. PBKDF2 com 600k iterações é o padrão OWASP 2023.)
 * - AES-GCM cifra/decifra o blob contendo a private key.
 *
 * Tudo é executado dentro do WebCrypto nativo do navegador.
 */
const PBKDF2_ITERATIONS = 600_000;
const SALT_LEN = 16;
const IV_LEN = 12;
const KEY_LEN_BITS = 256;

const enc = new TextEncoder();

/**
 * Copia o conteúdo de um Uint8Array pra um ArrayBuffer "puro".
 *
 * Necessário porque a partir do TS 5.7 o tipo de `Uint8Array#buffer` é
 * `ArrayBufferLike` (que inclui `SharedArrayBuffer`), enquanto WebCrypto
 * exige `BufferSource` com `ArrayBuffer` strict. Copiar pra um buffer
 * novo resolve o type-check e mantém o comportamento idêntico.
 */
function toBuffer(view: Uint8Array): ArrayBuffer {
  const ab = new ArrayBuffer(view.byteLength);
  new Uint8Array(ab).set(view);
  return ab;
}

async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    toBuffer(enc.encode(password)),
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: toBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: KEY_LEN_BITS },
    false,
    ["encrypt", "decrypt"],
  );
}

export interface EncryptedBlob {
  ciphertext: Uint8Array;
  salt: Uint8Array;
  iv: Uint8Array;
}

export async function encryptWithPassword(plaintext: Uint8Array, password: string): Promise<EncryptedBlob> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LEN));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
  const key = await deriveKey(password, salt);

  const buf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toBuffer(iv) },
    key,
    toBuffer(plaintext),
  );
  return {
    ciphertext: new Uint8Array(buf),
    salt,
    iv,
  };
}

export async function decryptWithPassword(blob: EncryptedBlob, password: string): Promise<Uint8Array> {
  const key = await deriveKey(password, blob.salt);
  try {
    const buf = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: toBuffer(blob.iv) },
      key,
      toBuffer(blob.ciphertext),
    );
    return new Uint8Array(buf);
  } catch {
    throw new InvalidPasswordError();
  }
}

export class InvalidPasswordError extends Error {
  constructor() {
    super("Senha inválida");
    this.name = "InvalidPasswordError";
  }
}
